import { DateTime } from '@ksyos/date-time';
import { Koi } from '@ksyos/koi';
import { Map } from 'immutable';
import type { ITimeRange } from './callBackMoment';
import type { ISchemaContainer } from './schemaContainer';
import { validateDatetimeOverlap } from './utils/validateDatetimeOverlap';

export interface ITimeSlots {
    firstTimeRange: ITimeRange;
    secondTimeRange: ITimeRange;
}

export const timeRangeValidator = Koi.object({
    endTime: Koi.koi().timeWithoutSeconds().allow(null, ''),
    startTime: Koi.koi().timeWithoutSeconds().allow(null, ''),
});

export const timeSlotsValidator = Koi.object({
    firstTimeRange: timeRangeValidator.allow(null),
    secondTimeRange: timeRangeValidator.allow(null),
});

export const availabilityValidator = Koi.object({
    customControlData: Koi.object({
        availability: timeSlotsValidator,
    }),
});

export const timeRangeValidatorSchema: ISchemaContainer<ITimeRange> = {
    schema: timeRangeValidator,
};

export const availabilityValidatorSchema: ISchemaContainer<ITimeSlots> = {
    schema: availabilityValidator,
};

export function areValidTimeSlots(timeSlots: ITimeSlots) {
    let errors: Map<string, string | undefined> = Map();
    let hasErrors = false;

    if (timeSlots.firstTimeRange) {
        errors = validateTimePeriod(timeSlots.firstTimeRange, errors, 'firstTimeRange');
        errors = validateTimePeriodOverlap(timeSlots, errors, 'firstTimeRange');
        hasErrors = !errors.isEmpty();
    }

    if (timeSlots.secondTimeRange && !hasErrors) {
        errors = validateTimePeriod(timeSlots.secondTimeRange, errors, 'secondTimeRange');
        errors = validateTimePeriodOverlap(timeSlots, errors, 'secondTimeRange');
        hasErrors = !errors.isEmpty();
    }

    return !hasErrors;
}

export function validateBothInputsFilled(
    timeRange: ITimeRange,
    errors: Map<string, string | undefined>,
    fieldKey: string
) {
    if (!!timeRange.startTime !== !!timeRange.endTime) {
        return errors.set(fieldKey, 'bothTimesRequired');
    }

    return errors;
}

export function validateTimePeriod(timeRange: ITimeRange, errors: Map<string, string | undefined>, fieldKey: string) {
    const oneHourInMinutes = 60;
    if (timeRange.startTime && timeRange.endTime) {
        const startDateTime = DateTime.parseExact(timeRange.startTime, 'HH:mm');
        const endDateTime = DateTime.parseExact(timeRange.endTime, 'HH:mm');
        if (startDateTime.isValid() && endDateTime.isValid()) {
            const minutesDiff = endDateTime.diff(startDateTime, 'minutes');
            if (minutesDiff < 0) {
                return errors.set(fieldKey, 'endTimeBeforeStartTime');
            } else if (minutesDiff < oneHourInMinutes) {
                return errors.set(fieldKey, 'minOneHourTimePeriodRequired');
            }
        }
    }

    return errors;
}

export function validateTimePeriodOverlap(
    availability: ITimeSlots,
    errors: Map<string, string | undefined>,
    fieldKey: string
) {
    if (
        !availability.firstTimeRange.startTime ||
        !availability.firstTimeRange.endTime ||
        !availability.secondTimeRange.startTime ||
        !availability.secondTimeRange.endTime
    ) {
        return errors;
    }

    const [resultOne, resultTwo] = validateDatetimeOverlap([
        transformToDateRange(availability.firstTimeRange),
        transformToDateRange(availability.secondTimeRange),
    ]);

    if (resultOne.errors.size > 0 || resultTwo.errors.size > 0) {
        return errors.set(fieldKey, 'timePeriodsOverlap');
    }

    return errors;
}

function transformToDateRange(timeRange: ITimeRange) {
    return {
        endDate: timeRange.endTime ? DateTime.parseExact(timeRange.endTime, 'HH:mm').toDate() : null,
        errors: Map<string, string | undefined>(),
        startDate: timeRange.startTime ? DateTime.parseExact(timeRange.startTime, 'HH:mm').toDate() : null,
    };
}
